import React, { useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box } from '@mui/material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const RichTextEditor = ({ value, onChange }: RichTextEditorProps) => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .ql-container {
        font-family: 'Poppins', sans-serif;
        border-radius: 0 0 8px 8px;
        border: 1px solid #e0e0e0;
        border-top: none;
      }
      .ql-editor {
        font-family: 'Poppins', sans-serif;
        font-size: 16px;
        line-height: 1.6;
        padding: 16px;
        min-height: 120px;
        color: #2c3e50;
      }
      .ql-toolbar {
        font-size: 14px;
        border: 1px solid #e0e0e0;
        border-radius: 8px 8px 0 0;
        background-color: #fafafa;
        padding: 8px;
      }
      .ql-toolbar button {
        padding: 8px;
        border-radius: 4px;
        margin: 2px;
        transition: all 0.2s ease;
      }
      .ql-toolbar button:hover {
        background-color: #e3f2fd;
        color: #1976d2;
      }
      .ql-toolbar button.ql-active {
        background-color: #1976d2;
        color: white;
      }
      .ql-snow .ql-picker {
        font-size: 14px;
        border-radius: 4px;
      }
      .ql-snow .ql-picker-label {
        border-radius: 4px;
        padding: 6px 8px;
      }
      .ql-snow .ql-picker-options {
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      .ql-snow .ql-stroke {
        stroke: #5a6c7d;
      }
      .ql-snow .ql-fill {
        fill: #5a6c7d;
      }
      .ql-snow .ql-picker-label:hover .ql-stroke,
      .ql-snow .ql-picker-label:hover .ql-fill {
        stroke: #1976d2;
        fill: #1976d2;
      }
      .ql-snow .ql-picker-label.ql-active .ql-stroke,
      .ql-snow .ql-picker-label.ql-active .ql-fill {
        stroke: #1976d2;
        fill: #1976d2;
      }
      .ql-editor.ql-blank::before {
        color: #9e9e9e;
        font-style: normal;
        font-weight: 400;
      }
      .ql-editor:focus {
        outline: none;
      }
      .ql-container.ql-snow {
        border-color: #e0e0e0;
      }
      .ql-container.ql-snow:hover {
        border-color: #3498db;
      }
      .ql-container.ql-snow.ql-focused {
        border-color: #3498db;
        box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box
      sx={{
        '& .ql-container': {
          borderRadius: '0 0 8px 8px',
        },
        '& .ql-toolbar': {
          borderRadius: '8px 8px 0 0',
        },
      }}
    >
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            [{ size: ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link', 'image'],
            ['clean'],
          ],
        }}
        formats={[
          'header',
          'size',
          'bold',
          'italic',
          'underline',
          'strike',
          'color',
          'background',
          'list',
          'bullet',
          'indent',
          'link',
          'image',
        ]}
      />
    </Box>
  );
};

export default RichTextEditor;
